import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Sensor from '../Sensor';
import Spinner from '../_common/Spinner';
import ConfirmationModal from '../_common/ConfirmationModal';
import {
    createSensorGroup,
    deleteSensorGroup,
    getSensorGroups,
    updateSensorGroup
} from '../../store/actions/sensorGroupsActions';
import './SensorGroups.scss';

const selector = state => ({
    t: state.language.translator,
    groups: state.sensorGroups.groups,
    pending: state.sensorGroups.pending,
    error: state.sensorGroups.error
});

const SensorGroups = () => {
    const dispatch = useDispatch();
    const {t, groups, pending, error} = useSelector(selector);
    const [expandedGroupIds, setExpandedGroupIds] = useState([]);
    const [editedGroup, setEditedGroup] = useState(null);
    const [groupName, setGroupName] = useState('');
    const [selectedSensorIds, setSelectedSensorIds] = useState([]);
    const [confirmation, setConfirmation] = useState(null);

    useEffect(() => {
        dispatch(getSensorGroups()).catch(() => {});
    }, [dispatch]);

    const startAdding = () => {
        setEditedGroup({id: null});
        setGroupName('');
        setSelectedSensorIds([]);
    };

    const startEditing = group => {
        setEditedGroup(group);
        setGroupName(group.groupName);
        setSelectedSensorIds(group.sensorIds || []);
    };

    const closeEditor = () => {
        setEditedGroup(null);
        setGroupName('');
        setSelectedSensorIds([]);
    };

    const toggleSensor = sensor => {
        setSelectedSensorIds(current =>
            current.includes(sensor.id)
                ? current.filter(sensorId => sensorId !== sensor.id)
                : [...current, sensor.id]
        );
    };

    const saveGroup = () => {
        const request = {
            groupName: groupName.trim(),
            sensorIds: selectedSensorIds
        };
        const saveRequest = editedGroup.id
            ? dispatch(updateSensorGroup(editedGroup.id, request))
            : dispatch(createSensorGroup(request));

        saveRequest
            .then(closeEditor)
            .catch(() => {});
        setConfirmation(null);
    };

    const requestSave = () => {
        if (!groupName.trim()) {
            return;
        }

        if (editedGroup.id) {
            setConfirmation({
                text: t.gettext('Do you want to save changes to this sensor group?'),
                confirmText: t.gettext('Save'),
                action: saveGroup
            });
        } else {
            saveGroup();
        }
    };

    const requestDelete = group => {
        setConfirmation({
            text: `${t.gettext('Do you want to delete sensor group')} "${group.groupName}"?`,
            confirmText: t.gettext('Delete'),
            action: () => {
                dispatch(deleteSensorGroup(group.id)).catch(() => {});
                setConfirmation(null);
            }
        });
    };

    const requestRemoveSensor = (group, sensor) => {
        setConfirmation({
            text: `${t.gettext('Do you want to remove')} "${sensor.name}" ${t.gettext('from sensor group')} "${group.groupName}"?`,
            confirmText: t.gettext('Remove from group'),
            action: () => {
                dispatch(updateSensorGroup(group.id, {
                    groupName: group.groupName,
                    sensorIds: group.sensorIds.filter(sensorId => sensorId !== sensor.id)
                })).catch(() => {});
                setConfirmation(null);
            }
        });
    };

    const toggleExpanded = groupId => {
        setExpandedGroupIds(current =>
            current.includes(groupId)
                ? current.filter(id => id !== groupId)
                : [...current, groupId]
        );
    };

    if (editedGroup) {
        return (
            <div className='sensorGroupEditor'>
                <div className='sensorGroupEditorHeader'>
                    <div>
                        <h1>{editedGroup.id ? t.gettext('Change sensor group') : t.gettext('Add sensor group')}</h1>
                        <label htmlFor='sensorGroupName'>{t.gettext('Group name')}</label>
                        <input
                            id='sensorGroupName'
                            value={groupName}
                            maxLength={15}
                            onChange={event => setGroupName(event.target.value)}
                        />
                        <span className='sensorGroupNameCounter'>{groupName.length}/15</span>
                    </div>
                    <div className='sensorGroupEditorActions'>
                        <button type='button' className='secondaryButton' onClick={closeEditor}>
                            {t.gettext('Cancel')}
                        </button>
                        <button
                            type='button'
                            className='primaryButton'
                            disabled={!groupName.trim() || pending}
                            onClick={requestSave}
                        >
                            {t.gettext('Save')}
                        </button>
                    </div>
                </div>
                <Sensor
                    groupSelection
                    selectedSensorIds={selectedSensorIds}
                    onSensorSelectionChange={toggleSensor}
                    onFinishSelection={requestSave}
                />
                {confirmation &&
                    <ConfirmationModal
                        text={confirmation.text}
                        closeMethod={() => setConfirmation(null)}
                        confirmMethod={confirmation.action}
                        confirmButtonText={confirmation.confirmText}
                    />
                }
            </div>
        );
    }

    return (
        <div className='sensorGroups'>
            <div className='sensorGroupsHeader'>
                <h1>{t.gettext('Sensor groups')}</h1>
                <button type='button' className='addSensorGroupButton' onClick={startAdding}>
                    <span className='material-icons'>add</span>
                    {t.gettext('Add sensor group')}
                </button>
            </div>

            {error && <div className='sensorGroupsError'>{t.gettext('Unable to load sensor groups')}</div>}
            {pending && groups.length === 0
                ? <div className='page'><Spinner size={300}/></div>
                : groups.length === 0
                    ? <div className='emptySensorGroups'>{t.gettext('No sensor groups')}</div>
                    : <div className='sensorGroupsList'>
                        {groups.map(group => {
                            const expanded = expandedGroupIds.includes(group.id);
                            return (
                                <div className='sensorGroupRow' key={group.id}>
                                    <div className='sensorGroupSummary'>
                                        <button
                                            type='button'
                                            className='expandSensorGroupButton'
                                            onClick={() => toggleExpanded(group.id)}
                                            aria-expanded={expanded}
                                        >
                                            <span className='material-icons'>
                                                {expanded ? 'expand_less' : 'expand_more'}
                                            </span>
                                            <span>{group.groupName}</span>
                                            <span className='sensorCount'>
                                                {group.sensorIds.length} {t.gettext('sensors')}
                                            </span>
                                        </button>
                                        <div className='sensorGroupActions'>
                                            <button type='button' onClick={() => startEditing(group)}>
                                                {t.gettext('Change')}
                                            </button>
                                            <button type='button' className='deleteButton' onClick={() => requestDelete(group)}>
                                                {t.gettext('Delete')}
                                            </button>
                                        </div>
                                    </div>
                                    {expanded &&
                                        <div className='sensorGroupSensors'>
                                            {group.sensors.length === 0
                                                ? <div className='emptyGroup'>{t.gettext('No sensors in group')}</div>
                                                : group.sensors.map(sensor =>
                                                    <div className='sensorInGroup' key={sensor.id}>
                                                        <span>{sensor.name}</span>
                                                        <button
                                                            type='button'
                                                            onClick={() => requestRemoveSensor(group, sensor)}
                                                        >
                                                            {t.gettext('Remove from group')}
                                                        </button>
                                                    </div>
                                                )
                                            }
                                        </div>
                                    }
                                </div>
                            );
                        })}
                    </div>
            }

            {confirmation &&
                <ConfirmationModal
                    text={confirmation.text}
                    closeMethod={() => setConfirmation(null)}
                    confirmMethod={confirmation.action}
                    confirmButtonText={confirmation.confirmText}
                />
            }
        </div>
    );
};

export default SensorGroups;
